import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { addDays, format, isSameDay } from 'date-fns';
import { match } from 'ts-pattern';

import { AuctionCard, TimerOld as Timer } from '@catawiki/cata-style';

import { LiveStreamCampaignStatus } from '@/shared/constants/auctions';
import { formatDayMonth } from '@/shared/formatter';
import { Auctioneer, AuctionStatus } from '@/shared/models/auction';
import { MockAuction } from '@/shared/stubs/auction';

import { ScrollableList } from '../ScrollableList';
import styles from './Row.module.scss';

export interface AuctionInput {
  auctionId: number;
}

export interface Auction {
  id: number;
  href: string;
  title: string;
  curator: Auctioneer;
  mainImage: string;
  thumbImages: [string, string, string];
  numberOfLots: number;
  status: AuctionStatus;
  startAt: string;
  closeAt: number;
  liveStatus?: LiveStreamCampaignStatus;
  liveStartDate?: string;
  isLiveStream: boolean;
  isLiveStreamEndingNow: boolean;
}

interface Props {
  auctionIds?: AuctionInput[];
  auctions?: Auction[];
  locale: string;
}

const formatLiveStreamDate = (
  liveStartDate: string,
  t: (key: string, options?: Record<string, unknown>) => string
) => {
  const date = new Date(liveStartDate);

  if (Number.isNaN(date.getTime())) {
    return t('auction_lane.live_stream', {
      defaultValue: 'Live stream',
    });
  }

  const referenceDate = new Date();
  const time = format(date, 'HH:mm');
  const formattedDate = format(date, 'MMM d, HH:mm');

  if (isSameDay(date, referenceDate)) {
    return t('auction_lane.live_stream_today', {
      defaultValue: `Live stream today ${time}`,
      time,
    });
  }

  if (isSameDay(date, addDays(referenceDate, 1))) {
    return t('auction_lane.live_stream_tomorrow', {
      defaultValue: `Live stream tomorrow ${time}`,
      time,
    });
  }

  return t('auction_lane.live_stream_on_date', {
    defaultValue: `Live stream on ${formattedDate}`,
    live_stream_date_time: formattedDate,
  });
};

const getTimerText = (
  auction: Auction,
  locale: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  hasHydrated: boolean
) => {
  if (auction.liveStatus === LiveStreamCampaignStatus.ONAIR) {
    return t('auction_lane.live_stream_ending_now', {
      defaultValue: 'Ending now!',
    });
  }

  if (
    auction.liveStatus === LiveStreamCampaignStatus.READY &&
    auction.liveStartDate
  ) {
    if (!hasHydrated) {
      return null;
    }

    return formatLiveStreamDate(auction.liveStartDate, t);
  }

  return t('closes_on', {
    closed_on_date: formatDayMonth(locale, new Date(auction.closeAt)),
  });
};

export const Row: FC<Props> = ({ locale, auctionIds = [], auctions = [] }) => {
  const { t } = useTranslation();
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  const items = match({
    hasIds: auctionIds.length > 0,
    hasItems: auctions.length > 0,
  })
    .with({ hasIds: true, hasItems: false }, () =>
      auctionIds.map((item) => MockAuction(locale, item.auctionId))
    )
    .otherwise(() => auctions);

  return (
    <ScrollableList
      data-testid="row"
      itemClassName={styles.item}
      arrowClassName={styles.arrow}
    >
      {items.map((auction) => {
        const timer = (
          <Timer timer={getTimerText(auction, locale, t, hasHydrated)} />
        );
        const subTitle = `${t('auction_lane.curated_by')} ${
          auction.curator.name
        }`;
        return (
          <AuctionCard
            key={auction.id}
            subTitle={subTitle}
            {...auction}
            isLiveStream={auction.isLiveStream}
            isLiveStreamEndingNow={auction.isLiveStreamEndingNow}
            timer={timer}
          />
        );
      })}
    </ScrollableList>
  );
};
